/**
 * Kingdom Factor Homepage
 * - Scroll-animated video hero with scroll indicator
 * - Member section (Join a Peer Group)
 * - Coach section (Become a KF Coach)
 * - Kingdom Factor Stories section (Tabs + Cards)
 * - Share Your Story CTA
 */

import { Link } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { ArrowRight, Check, ChevronDown } from "lucide-react"
import { useState } from "react"
import { HeroScrollVideo } from "@/components/ui/scroll-animated-video"
import { Button } from "@/components/ui/button"
import { Header } from "@/components/layout/Header"
import { Footer } from "@/components/layout/Footer"
import { ShareYourStoryDialog } from "@/components/ShareYourStoryDialog"

export default function Home({ seo, settings = {}, memberStories = [], coachStories = [] }) {
  const [activeTab, setActiveTab] = useState("members")
  const [storyDialogOpen, setStoryDialogOpen] = useState(false)
  const { scheduleCallUrl = "#" } = settings

  // Get stories based on active tab
  const filteredStories = activeTab === "members" ? memberStories : coachStories

  return (
    <>
      <SeoHead seo={seo} />

      <div className="bg-white text-zinc-900 min-h-screen">
        <Header activePage="home" scheduleCallUrl={scheduleCallUrl} />

        {/* Scroll Video Hero — video + overlay temporarily disabled until video content is ready.
            To restore: set media back to the video URL and scrollHeightVh={250} */}
        <HeroScrollVideo
          title={<>REAL CHRISTIAN<br />BUSINESS LEADERS</>}
          subtitle="REAL FAITH. REAL RESULTS."
          meta="600+ Christian Business Leaders in a Transformational Community"
          credits={
            <div className="flex flex-col items-center gap-6 w-full max-w-xs sm:max-w-none mx-auto">
              <p className="text-zinc-500 text-sm uppercase tracking-wider font-semibold">3 ways to join the KF movement</p>
              <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
                <Button variant="gradient" asChild className="w-full sm:w-auto">
                  <Link href="/member">
                    Join a Peer Group
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button variant="kf-outline" asChild className="w-full sm:w-auto">
                  <Link href="/coach">
                    Become a KF Coach
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button variant="gradient" asChild className="w-full sm:w-auto">
                  <Link href="/share-your-story">
                    Share Your Story
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
              <ChevronDown className="w-6 h-6 text-white/60 animate-bounce" />
            </div>
          }
          media={null}
          overlay={null}
          initialBoxSize={320}
          scrollHeightVh={0}
          style={{ paddingTop: "80px" }}
        />

        {/* ============================================
            MEMBER SECTION
        ============================================ */}
        <section id="members" className="bg-kf-gradient py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="min-w-0">
                <span className="font-display text-7xl text-white/20">01</span>
                <h2 className="font-display text-4xl md:text-5xl mb-6 -mt-4 text-white">
                  JOIN A<br />
                  KF PEER GROUP
                </h2>
                <p className="text-white/80 text-lg leading-relaxed mb-8 uppercase tracking-wider">
                  Top 5 Reasons Leaders Join a Kingdom Factor Peer Group
                </p>

                <div className="space-y-5 mb-8">
                  {[
                    {
                      stat: "90%+",
                      title: "Christian Community & Leadership Support",
                      desc: "90% of members joined because leadership can feel isolating and they wanted a trusted community of Christian leaders."
                    },
                    {
                      stat: "85%+",
                      title: "Integrating Faith with Business Leadership",
                      desc: "85% of members joined to learn how to lead their business according to biblical principles."
                    },
                    {
                      stat: "80%+",
                      title: "Personal & Spiritual Growth",
                      desc: "80% of members joined to grow as leaders, spouses, and followers of Christ."
                    },
                    {
                      stat: "70%+",
                      title: "Wise Counsel for Business and Leadership Decisions",
                      desc: "70% of members joined to gain advice and perspective from experienced leaders in different industries."
                    },
                    {
                      stat: "65%+",
                      title: "Accountability & Honest Conversations",
                      desc: "65% of members joined because they wanted a confidential space to discuss real leadership challenges."
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <span className="font-display text-2xl text-white/50 w-14 flex-shrink-0">{item.stat}</span>
                      <div>
                        <p className="text-white font-semibold">{item.title}</p>
                        <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Button variant="kf-secondary" size="lg" className="group w-full sm:w-auto whitespace-normal" asChild>
                  <Link href="/member">
                    Explore Kingdom Factor Membership
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>

              <div className="bg-white/10 backdrop-blur p-8 lg:p-12 hidden lg:flex flex-col justify-center">
                <p className="text-xl text-white/90 leading-relaxed mb-6 italic">
                  "Across every Kingdom Factor testimonial, one theme is clear: leaders join
                  because they don't want to lead alone. They want a trusted community of
                  Christian leaders who will challenge them, support them, and help them
                  pursue Christ-centered leadership."
                </p>
                <p className="text-white/50 text-sm">
                  — Insights from Kingdom Factor Member Testimonials
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================
            COACH SECTION
        ============================================ */}
        <section id="coaches" className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 bg-zinc-100 p-8 lg:p-12">
                <p className="text-zinc-500 text-sm uppercase tracking-wider mb-4">What You Get</p>
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { title: "Comprehensive Training", desc: "Full certification program with ongoing development" },
                    { title: "Proven Model", desc: "Battle-tested curriculum and facilitation framework" },
                    { title: "Community Support", desc: "Network of experienced coaches and mentors" },
                    { title: "Flexible Schedule", desc: "Build your practice around your life" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="w-4 h-4 text-kf-red mt-1 flex-shrink-0" />
                      <div>
                        <span className="text-zinc-900 font-medium">{item.title}</span>
                        <p className="text-zinc-500 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <span className="font-display text-7xl text-kf-red/20">02</span>
                <h2 className="font-display text-4xl md:text-5xl mb-6 -mt-4 text-zinc-900">
                  BECOME A<br />
                  <span className="text-kf-red">KF COACH</span>
                </h2>
                <p className="text-zinc-600 text-lg leading-relaxed mb-8">
                  Transform your business expertise into eternal impact. Lead peer groups, mentor entrepreneurs, and build your legacy.
                </p>
                <Button variant="gradient" size="lg" className="group w-full sm:w-auto" asChild>
                  <Link href="/coach">
                    Start Your Coaching Journey
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================
            STORIES SECTION - Tabs + Cards
        ============================================ */}
        <section id="stories" className="bg-zinc-100 py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Section Header */}
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl md:text-5xl text-zinc-900 mb-4">
                KINGDOM FACTOR <span className="text-kf-red">STORIES</span>
              </h2>
              <p className="text-zinc-600 max-w-2xl mx-auto">
                Discover how faith-driven leaders are integrating biblical principles into their businesses—and seeing transformation.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-12">
              {[
                { id: "members", label: "KF Members" },
                { id: "coaches", label: "KF Coaches" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all ${
                    activeTab === tab.id
                      ? "bg-kf-gradient text-white"
                      : "bg-white text-zinc-600 hover:text-kf-red border border-zinc-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStories.map((article) => (
                <Link
                  key={article.id}
                  href={`/collective/${article.category.slug}/${article.slug}`}
                  className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow group block"
                >
                  <div className="aspect-[2/1] relative overflow-hidden">
                    <img
                      src={article.featuredImageUrl || "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=300&fit=crop"}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <span className="absolute top-3 left-3 px-3 py-1 bg-kf-red text-white text-xs font-semibold uppercase tracking-wider">
                      {article.category.name}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-lg text-zinc-900 mb-2">{article.title}</h3>
                    <p className="text-zinc-600 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-3">
                      {article.author.profileImageUrl ? (
                        <img
                          src={article.author.profileImageUrl}
                          alt={article.author.name}
                          className="w-10 h-10 rounded-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center">
                          <span className="text-zinc-500 font-semibold text-sm">
                            {article.author.name?.charAt(0) || "?"}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-zinc-900 text-sm">{article.author.name}</p>
                        <p className="text-zinc-500 text-xs">{article.displayCompany}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* View All Link */}
            <div className="text-center mt-10">
              <Link
                href={activeTab === "members" ? "/collective/kf-member-stories" : "/collective/kf-coaches"}
                className="inline-flex items-center gap-2 text-kf-red font-semibold uppercase tracking-wider text-sm hover:underline"
              >
                {activeTab === "members" ? "View All Member Stories" : "View All Coach Stories"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Share Your Story CTA */}
        <section className="bg-zinc-100 py-16 border-y border-zinc-200">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl md:text-3xl mb-4 text-zinc-900">
              THE KF COLLECTIVE
            </h2>
            <p className="text-zinc-600 mb-6">
              Feature your faith/business integration story on The KF Collective. Join us in building the leading collection of stories of Christian business leaders available.
            </p>
            <Button variant="gradient" size="lg" className="w-full sm:w-auto" onClick={() => setStoryDialogOpen(true)}>
              Share Your Story
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </section>

        <ShareYourStoryDialog
          open={storyDialogOpen}
          onOpenChange={setStoryDialogOpen}
        />

        <Footer activePage="home" />
      </div>
    </>
  )
}
